import type React from 'react';

import type { EventId } from '@vlabs/api/luna-api/model/EventId';
import { GeoLocation, GeoLocationMarker } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { useEventQuery } from '@vlabs/monokit/entities/events/api';

import st from './EventDetailsGeolocation.module.sass';
import { useEventDetailsGeolocationTranslation } from './i18n';
import { buildTestid } from '../../../shared/lib/utils';
import { GeneralEventInfoRow } from '../../general-event';

interface EventDetailsGeolocationProps extends WithDataTestid {
  eventId: EventId;
}

const displayName = 'EventDetailsGeolocation';
const testid = buildTestid(displayName);

function hasValue(value: unknown): boolean {
  return value != null && value !== '';
}

function EventDetailsGeolocation({
  eventId,
  'data-testid': dataTestId = undefined,
}: EventDetailsGeolocationProps): React.JSX.Element {
  const { t } = useEventDetailsGeolocationTranslation();
  const { data: event } = useEventQuery(eventId);

  const location = event?.location;
  const latitude = location?.geo_position?.latitude;
  const longitude = location?.geo_position?.longitude;
  const hasCoordinates = hasValue(latitude) && hasValue(longitude);
  const address = [
    location?.city,
    location?.area,
    location?.district,
    location?.street,
    location?.house_number,
  ].filter(hasValue).join(', ');
  const center: [number, number] = hasCoordinates
    ? [Number(latitude), Number(longitude)]
    : [0, 0];
  const marker = hasCoordinates ? <GeoLocationMarker anchor={center} /> : undefined;
  const addressContent = address || <span className={st.Empty}>{t('address_empty')}</span>;

  return (
    <div
      className={st.EventDetailsGeolocation}
      {...testid(dataTestId)}
    >
      <h6 className={st.Title}>{t('title')}</h6>

      <div className={st.Map}>
        <GeoLocation center={center}>
          {marker}
        </GeoLocation>
      </div>

      <div className={st.Info}>
        <div
          className={st.Address}
          {...testid('Address', dataTestId)}
        >
          {addressContent}
        </div>
        <GeneralEventInfoRow
          className={st.InfoRow}
          title={t('longitude')}
          {...testid('Longitude', dataTestId)}
        >
          {longitude}
        </GeneralEventInfoRow>
        <GeneralEventInfoRow
          className={st.InfoRow}
          title={t('latitude')}
          {...testid('Latitude', dataTestId)}
        >
          {latitude}
        </GeneralEventInfoRow>
      </div>
    </div>
  );
}

EventDetailsGeolocation.displayName = displayName;

export { EventDetailsGeolocation };
